import { useEffect, useState } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  lookupChannelItems,
  type ChannelItemLookupEntry,
} from '../api/marketplaceApi';
import type { ChannelItemType } from 'types/channel';

export interface UseChannelItemManageAccessArgs {
  itemType: ChannelItemType;
  itemSystemName: string | null | undefined;
}

export interface UseChannelItemManageAccessResult {
  /** True when the current user has the `manage` permission on at least one publishing channel_item. */
  canManage: boolean;
  /** The manageable channel_item — populated when `canManage` is true. */
  manageableItem: ChannelItemLookupEntry | null;
  loading: boolean;
}

/**
 * Look up the channel_items publishing this content and check the current
 * user's SpiceDB `manage` permission on each. Returns the first manageable
 * entry, if any — used by consumer views to gate a "Manage" affordance.
 *
 * Fails closed: returns `canManage: false` on any error or missing client.
 */
export function useChannelItemManageAccess({
  itemType,
  itemSystemName,
}: UseChannelItemManageAccessArgs): UseChannelItemManageAccessResult {
  const apiClients = useApiClientsOrNull();
  const [canManage, setCanManage] = useState(false);
  const [manageableItem, setManageableItem] =
    useState<ChannelItemLookupEntry | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!apiClients?.schemaClient || !apiClients?.permissionClient || !itemSystemName) {
      setCanManage(false);
      setManageableItem(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const items = await lookupChannelItems(
          apiClients.schemaClient,
          itemType,
          itemSystemName
        );

        for (const item of items) {
          if (cancelled) return;
          try {
            // channel_item is not a sphere — call /permission_list directly
            // (fetchPermissionList from spheres/ routes to /spheres/permissions which handles spheres only)
            const perms = await (
              apiClients.permissionClient as unknown as {
                post: <T>(path: string, data: unknown) => Promise<T>;
              }
            ).post<{ permissions: Record<string, boolean> }>('/permission_list', {
              resourceType: 'channel_item',
              resourceId: item.id,
              permissions: ['manage'],
            });
            if (perms?.permissions?.['manage']) {
              if (!cancelled) {
                setCanManage(true);
                setManageableItem(item);
                setLoading(false);
              }
              return;
            }
          } catch {
            // Ignore per-item permission failures — continue scanning the rest.
          }
        }

        if (!cancelled) {
          setCanManage(false);
          setManageableItem(null);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setCanManage(false);
          setManageableItem(null);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiClients, itemType, itemSystemName]);

  return { canManage, manageableItem, loading };
}

export default useChannelItemManageAccess;
